/**
 * Institutional proof.
 *
 * From `lg` up all eight marks fit on one line, so they are laid out as a
 * static row inside the container: nothing moves, nothing is clipped on
 * first paint, and the strongest proof on the page can be read. Below `lg`
 * the same marks run as the infinite marquee, bleeding to the viewport edges.
 *
 * Marquee mechanics: the track holds two copies and slides by exactly -50%,
 * so the loop is seamless only if each copy is exactly half the track. The
 * gap is therefore a per-item margin rather than a flex gap. The second copy
 * is aria-hidden and out of the tab order; the track pauses while any link
 * inside it has focus (see .pp-marquee in src/styles/components.css).
 */
import React from "react";
import PropTypes from "prop-types";
import { useLocales } from "../../core/i18n";

/** The logo strip: a static row from lg up, a marquee below. */
const LogoShowcase = ({ logos, bleed = 0 }) => {
	const { t } = useLocales();

	return (
		<div role="group" aria-label={t("ui.logoMarqueeLabel")}>
			<ul className="hidden lg:flex items-center justify-between list-none">
				{logos.map((logo) => (
					<li key={`s-${logo.alt}`}>
						<LogoItem {...logo} spacing="" />
					</li>
				))}
			</ul>

			{/* Marquee - below lg. bleed pulls it to the viewport edges; the same
          value pads the clipping wrapper so the first mark starts on the grid. */}
			<div
				className="lg:hidden"
				style={{
					overflow: "hidden",
					marginLeft: -bleed,
					marginRight: -bleed,
					paddingLeft: bleed,
					// Marks fade at both viewport edges instead of being cut mid-letter.
					WebkitMaskImage: `linear-gradient(to right, transparent, #000 ${bleed}px, #000 calc(100% - ${bleed}px), transparent)`,
					maskImage: `linear-gradient(to right, transparent, #000 ${bleed}px, #000 calc(100% - ${bleed}px), transparent)`,
				}}
			>
				<div className="pp-marquee flex items-center" style={{ width: "max-content" }}>
					{logos.map((logo, index) => (
						<LogoItem key={`a-${index}`} {...logo} />
					))}
					{logos.map((logo, index) => (
						<LogoItem key={`b-${index}`} {...logo} decorative />
					))}
				</div>
			</div>
		</div>
	);
};

const LogoItem = ({
	src,
	alt,
	link,
	decorative = false,
	scale = 1,
	spacing = "mr-12 sm:mr-20",
}) => {
	// Every mark is sized by height, not boxed, so a square seal and a wide
	// wordmark read at the same optical weight. 40px on small screens, 48px in
	// the static row.
	const img = (
		<div className="pp-logo flex items-center justify-center flex-shrink-0 h-8 sm:h-10 lg:h-12">
			<img
				src={src}
				alt={decorative ? "" : alt}
				className="w-auto max-w-[120px] sm:max-w-[150px] object-contain mix-blend-multiply"
				style={{ height: `${scale * 100}%` }}
				loading="lazy"
				draggable={false}
			/>
		</div>
	);

	// py-2 lifts each link's hit area to 48px+ without changing the mark's size.
	const cls = `flex-shrink-0 inline-flex items-center py-2 ${spacing}`.trim();

	if (link) {
		return (
			<a
				href={link}
				target="_blank"
				rel="noopener noreferrer"
				aria-label={decorative ? undefined : `Visit ${alt} website`}
				aria-hidden={decorative || undefined}
				tabIndex={decorative ? -1 : undefined}
				className={cls}
			>
				{img}
			</a>
		);
	}
	return (
		<div className={cls} aria-hidden={decorative || undefined}>
			{img}
		</div>
	);
};

LogoShowcase.propTypes = {
	/** Horizontal bleed (px) for the marquee variant, matching the container gutter. */
	bleed: PropTypes.number,
	logos: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string.isRequired,
			alt: PropTypes.string.isRequired,
			link: PropTypes.string,
			scale: PropTypes.number,
		}),
	).isRequired,
};

LogoItem.propTypes = {
	src: PropTypes.string.isRequired,
	alt: PropTypes.string.isRequired,
	link: PropTypes.string,
	decorative: PropTypes.bool,
	/** Optical height correction (1 = the strip height). */
	scale: PropTypes.number,
	spacing: PropTypes.string,
};

export default LogoShowcase;
